import { IconButton, Tooltip } from '@material-ui/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BreadCrumb from '../../../component/BreadCrumb';
import { CODE_GROUP_ID } from '../../../constant/Constant';
import CodeUtil from '../../../util/CodeUtil';
import PopupRef from '../../../component/PopupRef';
import SystemTermModal from '../../modal/SystemTermModal';
import UniversitySemesterModal from '../../modal/UniversitySemesterModal';
import { modalType } from '../../../util/ModalUtil';
import ModalRef from '../../../component/ModalRef';
import SelectionBox from '../../../component/SelectionBox';

import DeleteIcon from '../../../component/icon/DeleteIcon';
import RegistButtonBox from '../../../component/buttons/RegistButtonBox';
import TableBox from '../../../component/TableBox';
import { actionCurrentSemester, actionRemove, actionSearch } from '../../../module/body/system/SystemTermModule';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { VIEW_SYSTEM_TERM } from './../../../constant/ViewConstant';
import SearchButtonBox from '../../../component/buttons/SearchButtonBox';
import { formatDateTimeString } from '../../../util/TimeTableUtil';
import NullCheckUtil from '../../../util/NullCheckUtil';
import SessionStorageUtil from '../../../util/SessionStorageUtil';
import { defaultGridData, handleControl, initGrid } from '../../../module/component/TableBoxModule';
import { OpenEditIcon } from '../../../component/icon/GridIcons';
import PathUtil from '../../../util/PathUtil';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import { Link } from '@mui/material';

const ConditionArea = ({ popupRef, modalRef }) => {
    const location = useLocation();
    const queryData = qs.parse(location.search, { ignoreQueryPrefix: true });
    const dispatch = useDispatch();
    // condition
    const [semesterYearCd, setSemesterYearCd] = useState('');
    const [semesterTypeCd, setSemesterTypeCd] = useState('');
    const [params, setParams] = useState({});

    const { gridData } = useSelector((state) => state.tableBoxState);

    useEffect(() => {
        dispatch(
            handleControl({
                columns,
                onRowDoubleClick,
                onChangePageInfo,
                params,
            }),
        );

        queryData.semesterYearCd && setSemesterYearCd(queryData.semesterYearCd);
        queryData.semesterTypeCd && setSemesterTypeCd(queryData.semesterTypeCd);

        onClickSearch({
            semesterYearCd: queryData.semesterYearCd,
            semesterTypeCd: queryData.semesterTypeCd,
            page: NullCheckUtil.isNotNull(queryData.page) ? queryData.page : defaultGridData.page,
            pageSize: NullCheckUtil.isNotNull(queryData.pageSize) ? queryData.pageSize : 100,
        });

        return () => {
            dispatch(initGrid());
        };
    }, []);

    useEffect(() => {
        dispatch(
            handleControl({
                columns,
                onRowDoubleClick,
                onChangePageInfo,
                params,
            }),
        );
    }, [params]);

    const onChangePageInfo = (page, pageSize) => {
        onClickSearch({
            ...params,
            page,
            pageSize,
        });
    };
    // button action - search
    const onClickSearch = (params) => {
        setParams(params);
        dispatch(
            actionSearch({
                params,
                callback: (result) => {
                    if (result.totalCount == 0) {
                        popupRef.current.openAlert('조회된 결과가 없습니다.');
                    }
                },
            }),
        );
        PathUtil.pushState(location.pathname, params);
    };

    const onRowDoubleClick = (row, params) => {
        modalRef.current.open(
            <SystemTermModal
                type={modalType.EDIT}
                row={row}
                callback={() => {
                    modalRef.current.close();
                    popupRef.current.openInfo('학기정보가 저장되었습니다.');
                    onClickSearch(params);
                }}
                modalRef={modalRef}
            />,
            'medium',
        );
    };

    const onClickUniversitySemesterList = (row) => {
        modalRef.current.open(
            <UniversitySemesterModal semesterId={row.semesterId} semesterNm={row.semesterNm} modalRef={modalRef} />,
            'medium',
        );
    };

    // grid action - delete
    const handleDelete = (row, params) => {
        if (NullCheckUtil.isNotEmpty(row.currentSemesterId)) {
            popupRef.current.openInfo('현재학기 정보는 삭제할 수 없습니다.');
        } else {
            popupRef.current.openWarning('학기 ' + row.semesterNm + '(를)을 삭제하시겠습니까?', () => {
                return function () {
                    dispatch(
                        actionRemove({
                            data: { semesterId: row.semesterId },
                            callback: () => {
                                popupRef.current.openInfo('학기정보가 삭제되었습니다.');
                                onClickSearch(params);
                            },
                        }),
                    );
                };
            });
        }
    };

    const handleCurrentSemester = (row) => {
        popupRef.current.openWarning('학기 ' + row.semesterNm + '(를)을 현재학기로 설정하시겠습니까?', () => {
            return function () {
                dispatch(
                    actionCurrentSemester({
                        data: { propValue: row.semesterId, propNm: row.semesterNm },
                        callback: () => {
                            SessionStorageUtil.setCurrentSemester({
                                id: 0,
                                propKey: 'CURRENT-SEMESTER',
                                propNm: row.semesterNm,
                                propValue: row.semesterId,
                            });
                            popupRef.current.openInfo('학기 ' + row.semesterNm + '(가)이 현재학기로 설정되었습니다.');
                            onClickSearch(params);
                        },
                    }),
                );
            };
        });
    };

    const handleAdd = (params) => {
        modalRef.current.open(
            <SystemTermModal
                type={modalType.ADD}
                callback={() => {
                    modalRef.current.close();
                    popupRef.current.openInfo('학기정보가 등록되었습니다.');
                    onClickSearch(params);
                }}
                modalRef={modalRef}
            />,
            'medium',
        );
    };

    const onKeyDown = (e) => {
        if (e.key == 'Enter') {
            onClickSearch({
                semesterYearCd,
                semesterTypeCd,
                page: defaultGridData.page,
                pageSize: gridData.pageSize,
            });
        }
    };

    const columns = useMemo(
        () => [
            {
                field: 'semesterYearCd',
                headerName: '학기년도',
                headerAlign: 'center',
                align: 'center',
                tooltip: true,
                flex: 10,
                renderCell: (params) => CodeUtil.getCodeName(CODE_GROUP_ID.SEMESTER_YEAR, params.row.semesterYearCd),
            },
            {
                field: 'semesterTypeCd',
                headerName: '학기타입',
                headerAlign: 'center',
                align: 'center',
                tooltip: true,
                flex: 10,
                renderCell: (params) => CodeUtil.getCodeName(CODE_GROUP_ID.SEMESTER_TYPE, params.row.semesterTypeCd),
            },
            {
                field: 'currentSemesterId',
                headerName: '현재학기',
                headerAlign: 'center',
                align: 'center',
                flex: 5,
                renderCell: (params) =>
                    params.row.currentSemesterId == params.row.semesterId ? (
                        <Tooltip title={params.row.currentSemesterNm}>
                            <CheckCircleOutlineIcon color='primary' />
                        </Tooltip>
                    ) : (
                        <></>
                    ),
            },
            {
                field: 'universitySemesterList',
                headerName: '대학별상세',
                headerAlign: 'center',
                align: 'center',
                flex: 7,
                renderCell: (params) => (
                    <Link
                        component='button'
                        onClick={(e) => {
                            onClickUniversitySemesterList(params.row);
                        }}
                    >
                        상세
                    </Link>
                ),
            },
            {
                field: 'subjectRegEndMillis',
                headerName: '과목등록 종료',
                headerAlign: 'center',
                align: 'center',
                flex: 10,
                tooltip: true,
                renderCell: (params) => formatDateTimeString(params.row.subjectRegEndMillis),
            },
            // 수강신청 관련
            {
                field: 'enrolmentReqBgnMillis',
                headerName: '수강신청 시작',
                headerAlign: 'center',
                align: 'center',
                tooltip: true,
                flex: 10,
                renderCell: (params) => formatDateTimeString(params.row.enrolmentReqBgnMillis),
            },
            {
                field: 'enrolmentReqEndMillis',
                headerName: '수강신청 종료',
                headerAlign: 'center',
                align: 'center',
                tooltip: true,
                flex: 10,
                renderCell: (params) => formatDateTimeString(params.row.enrolmentReqEndMillis),
            },
            {
                field: 'enrolmentValidEndMillis',
                headerName: '수강검증 종료',
                headerAlign: 'center',
                align: 'center',
                tooltip: true,
                flex: 10,
                renderCell: (params) => formatDateTimeString(params.row.enrolmentValidEndMillis),
            },
            {
                field: 'enrolmentRegEndMillis',
                headerName: '수강등록 종료',
                headerAlign: 'center',
                align: 'center',
                tooltip: true,
                flex: 10,
                renderCell: (params) => formatDateTimeString(params.row.enrolmentRegEndMillis),
            },
            {
                field: 'detail',
                headerName: '기능',
                headerAlign: 'center',
                align: 'center',
                flex: 18,
                disableColumnMenu: true,
                hideSortIcons: true,
                disableExport: true,
                renderCell: (item) => (
                    <>
                        <Tooltip title='수정'>
                            <IconButton onClick={() => onRowDoubleClick(item.row, params)}>
                                <OpenEditIcon />
                            </IconButton>
                        </Tooltip>
                        {NullCheckUtil.isNotEmpty(item.row.currentSemesterId) ? (
                            <IconButton disabled>
                                <CheckCircleOutlineIcon />
                            </IconButton>
                        ) : (
                            <IconButton>
                                <Tooltip title={'현재학기로 설정'}>
                                    <CheckCircleOutlineIcon onClick={(e) => handleCurrentSemester(item.row)} />
                                </Tooltip>
                            </IconButton>
                        )}

                        <Tooltip title={'삭제'}>
                            <IconButton onClick={(e) => handleDelete(item.row, params)}>
                                <DeleteIcon />
                            </IconButton>
                        </Tooltip>
                    </>
                ),
            },
        ],
        [params],
    );

    return (
        <>
            <div className='con-form-wrap '>
                <div className='board-inner'>
                    <div className='board-row'>
                        <div className='lec-check-box'>
                            <div className='search-condition-box'>
                                <div className='form-box-container'>
                                    <SelectionBox
                                        title='학기년도'
                                        w140
                                        options={CodeUtil.getCodes(CODE_GROUP_ID.SEMESTER_YEAR)}
                                        value={semesterYearCd}
                                        onChange={(e) => setSemesterYearCd(e.target.value)}
                                        onKeyDown={(e) => onKeyDown(e)}
                                    />
                                    <SelectionBox
                                        title='학기타입'
                                        w140
                                        options={CodeUtil.getCodes(CODE_GROUP_ID.SEMESTER_TYPE)}
                                        value={semesterTypeCd}
                                        onChange={(e) => setSemesterTypeCd(e.target.value)}
                                        onKeyDown={(e) => onKeyDown(e)}
                                    />
                                </div>
                            </div>
                            <div className='lec-check-btn'>
                                <SearchButtonBox
                                    title='조회'
                                    onClick={(e) =>
                                        onClickSearch({
                                            semesterYearCd,
                                            semesterTypeCd,
                                            page: defaultGridData.page,
                                            pageSize: gridData.pageSize,
                                        })
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='con-tb-wrap'>
                <div className='con-tb-title  '>
                    <h3 className='tb-tit-name'>학기 목록</h3>
                    <RegistButtonBox onClick={(e) => handleAdd(params)} />
                </div>
            </div>
        </>
    );
};

const SystemTermView = () => {
    const popupRef = useRef(null);
    const modalRef = useRef(null);

    return (
        <section className='container'>
            <div className='container-inner'>
                <div className='con-title-wrap conbg02'>
                    {/* <h2 className='con-title'>학기 관리</h2> */}
                    <h2 className='con-title'>{VIEW_SYSTEM_TERM.title}</h2>
                    <p className='con-title-p'>{VIEW_SYSTEM_TERM.subTitle}</p>
                </div>
                <div className='sub-nav-wrap'>
                    <BreadCrumb />
                </div>
                <ConditionArea popupRef={popupRef} modalRef={modalRef} />
                <TableBox />
            </div>
            <ModalRef ref={modalRef} />
            <PopupRef ref={popupRef} />
        </section>
    );
};

export default SystemTermView;
